import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import { Marquee } from '@sorare/core/src/atoms/container/Marquee';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { useConnectionContext } from '@sorare/core/src/contexts/connection';
import { glossary } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { RigidSquareTitle } from 'components/landing/ui/RigidSquareTitle';

import fieldAvif from './assets/field.avif';
import field from './assets/field.jpg';
import mbappeAvif from './assets/mbappe.avif';
import mbappe from './assets/mbappe.png';
import ribonBackAvif from './assets/ribon-back.avif';
import ribonBack from './assets/ribon-back.jpg';
import ribonAvif from './assets/ribon.avif';
import ribon from './assets/ribon.jpg';

const Root = styled.section`
  position: relative;
  isolation: isolate;
`;
const Grid = styled.div`
  position: relative;
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  padding: var(--quadruple-unit) var(--double-unit) 0;
  background-image: url(${field}});
  background-image: image-set(
    url(${fieldAvif}) type('image/avif'),
    url(${field})
  );
  background-size: cover;
  background-position: center;
  &:before {
    position: absolute;
    pointer-events: none;
    content: '';
    inset: 0;
    background-image: linear-gradient(
        to top,
        var(--c-neutral-100),
        transparent 16%
      ),
      linear-gradient(to bottom, var(--c-neutral-100), transparent 16%);
    z-index: 1;
  }
  @media ${laptopAndAbove} {
    display: grid;
    justify-content: center;
    gap: var(--double-unit);
    grid-template-columns: minmax(0, 725px) 536px;
  }
`;

const Kylian = styled.span`
  font-size: 150%;
`;
const Content = styled(Vertical)`
  align-items: center;
  z-index: 1;
  text-align: center;
  @media ${laptopAndAbove} {
    align-items: flex-start;

    text-align: left;
  }
`;
const Ribbon = styled.div`
  position: relative;
  z-index: 2;
  font-family: var(--rigid-square);
  font-size: 43px;
  letter-spacing: 0.08em;
  font-weight: bold;
  transform: rotate(-0.7deg);
  text-transform: uppercase;
  &::before,
  &::after {
    position: absolute;
    inset: 0;
    z-index: -1;
    content: '';
  }
  &:before {
    transform: rotate(-1deg);
    background-image: image-set(
      url(${ribonBackAvif}) type('image/avif'),
      url(${ribonBack})
    );
  }
  &:after {
    background-image: image-set(
      url(${ribonAvif}) type('image/avif'),
      url(${ribon})
    );
  }
`;
export const Needsyou = () => {
  const { signUp } = useConnectionContext();
  return (
    <Root>
      <Ribbon>
        <Marquee direction="reverse" duration="10s">
          <FormattedMessage
            id="Kylianmbappe.NeedsYou.marquee"
            defaultMessage="Win the competition to manage Kylian's team"
          />
        </Marquee>
      </Ribbon>
      <Grid>
        <picture>
          <source srcSet={mbappeAvif} type="image/avif" />
          <Img src={mbappe} fetchpriority="low" width="725" height="831" />
        </picture>
        <Content gap={3}>
          <Chip
            uppercased
            outlined
            size="smaller"
            color="black"
            label={
              <Horizontal>
                <FormattedDate
                  value={new Date('2024-09-10T00:00:00Z')}
                  day="numeric"
                  month="short"
                />
                <span>-</span>
                <FormattedDate
                  value={new Date('2024-09-20T00:00:00Z')}
                  day="numeric"
                  month="short"
                />
              </Horizontal>
            }
          />
          <RigidSquareTitle className="left">
            <FormattedMessage
              id="kylianmbappe.NeedsYou.title"
              defaultMessage="<bold>Kylian</bold>needs you"
              values={{
                bold: c => <Kylian>{c}</Kylian>,
              }}
            />
          </RigidSquareTitle>
          <BodyL>
            <FormattedMessage
              id="kylianmbappe.NeedsYou.desc"
              defaultMessage="Kylian keeps losing to his brother and is looking for the best manager to take over his Sorare team. Win the competition, become his team’s new manager, and get rewarded!"
            />
          </BodyL>
          <Button color="secondary" size="large" onClick={signUp}>
            <FormattedMessage {...glossary.playNow} />
          </Button>
        </Content>
      </Grid>
    </Root>
  );
};
